import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { api, type FeedCollection, type FeedImage, type Product } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { CollectionCover } from "@/components/feed/collection-cover";
import { BagIcon, ChevronLeftIcon, FavoriteHeartIcon } from "@/components/icons";
import { IdeaActions } from "@/components/idea/idea-actions";
import { PhotoZoom } from "@/components/product/photo-zoom";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackCollectionView, trackProductView } from "@/lib/discovery";
import { DiscoverySourceProvider } from "@/lib/discovery-source";
import { IdeaOriginProvider, productHref, rememberIdeaOrigin, useIdeaOrigin } from "@/lib/idea-origin";
import { cn } from "@/lib/utils";
import { useFormatPrice } from "@/lib/use-format-price";
import { ideaLikeId, useCart, useWishlist } from "@/store";
import { useVisibleIdeas } from "@/store/hidden-ideas";
import { Skeleton } from "@osmix/ui/skeleton";
import { QuickViewModal } from "@/components/product/quick-view";
import { IdeaCard, IdeaWall, SaveHeartButton } from "./feed";
import { notifyAddedToCart } from "@/lib/cart-toast";
import { usePageMeta } from "@/lib/use-page-meta";
import { orderQty } from "@/lib/min-order";
import { sizeChipLabel } from "@/lib/product-size";

// A single inspiration idea: the pinned cover gallery on the left, the idea
// meta + a shoppable «Товары из идеи» panel on the right, and a «Похожие идеи»
// wall below (idea_h.png / idea_v.png / idea_cart.png). Cover hotspots still
// link straight to individual products.
export function FeedCollectionPage() {
  const $ = _c(9);
  const {
    slug: t0
  } = useParams();
  const slug = t0 === undefined ? "" : t0;
  usePageMeta();
  let t1;
  if ($[0] !== slug) {
    t1 = {
      queryKey: ["feed", "collection", slug],
      queryFn: () => api.feed.collection(slug)
    };
    $[0] = slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const col = useQuery(t1);
  if (col.isPending) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton className="h-5 w-28 rounded-full" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex flex-col gap-8">{t2}<div className="grid gap-8 lg:grid-cols-2"><Skeleton className="aspect-[280/377] w-full rounded-[8px]" /><Skeleton className="h-96 w-full rounded-[20px]" /></div></div>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  if (col.error || !col.data) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <BackLink />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== col) {
      t3 = <div className="flex flex-col gap-6">{t2}<ErrorState onRetry={() => col.refetch()} /></div>;
      $[5] = col;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t2;
  if ($[7] !== col.data) {
    t2 = <DiscoverySourceProvider value="inspiration_feed"><CollectionView key={col.data.id} collection={col.data} /></DiscoverySourceProvider>;
    $[7] = col.data;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function CollectionView(t0) {
  const $ = _c(92);
  const {
    collection: c
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== c.id) {
    t1 = ["products", "byIds", c.id];
    $[0] = c.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== c.productIds) {
    t2 = () => api.products.byIds(c.productIds);
    $[2] = c.productIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const products = useQuery(t3);
  let t4;
  let t5;
  if ($[7] !== c.id || $[8] !== c.slug) {
    t4 = () => {
      trackCollectionView(c.slug, c.id);
    };
    t5 = [c.slug, c.id];
    $[7] = c.id;
    $[8] = c.slug;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== c.cover || $[12] !== c.hotspots || $[13] !== c.images) {
    t6 = c.images.length > 0 ? c.images : [{
      url: c.cover,
      hotspots: c.hotspots
    }];
    $[11] = c.cover;
    $[12] = c.hotspots;
    $[13] = c.images;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const gallery = t6;
  const [active, setActive] = useState(0);
  const current = Math.min(active, gallery.length - 1);
  const activeImage = gallery[current];
  const [zoomOpen, setZoomOpen] = useState(false);
  const [landscape, setLandscape] = useState(false);
  let t7;
  if ($[15] !== c.id || $[16] !== c.slug) {
    t7 = {
      collectionId: c.id,
      collectionSlug: c.slug
    };
    $[15] = c.id;
    $[16] = c.slug;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const origin = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <BackLink />;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== gallery) {
    t9 = gallery.map(_temp);
    $[19] = gallery;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setZoomOpen(false);
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== current || $[23] !== t9 || $[24] !== zoomOpen) {
    t11 = <PhotoZoom images={t9} index={current} open={zoomOpen} onClose={t10} onIndex={setActive} />;
    $[22] = current;
    $[23] = t9;
    $[24] = zoomOpen;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setZoomOpen(true);
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t) {
    t13 = t("feed.zoomImage", {
      defaultValue: "\u0423\u0432\u0435\u043B\u0438\u0447\u0438\u0442\u044C \u0444\u043E\u0442\u043E"
    });
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e => setLandscape(e.currentTarget.naturalWidth > e.currentTarget.naturalHeight);
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = landscape ? "aspect-[16/9]" : "aspect-[280/377]";
  let t16;
  if ($[30] !== t15) {
    t16 = cn("rounded-[8px] object-cover", t15);
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== activeImage || $[33] !== c || $[34] !== t16) {
    t17 = <CollectionCover collection={c} image={activeImage} hideBadge={true} hideHotspots={true} onImgLoad={t14} imgClassName={t16} />;
    $[32] = activeImage;
    $[33] = c;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t13 || $[37] !== t17) {
    t18 = <button type="button" onClick={t12} aria-label={t13} className="block w-full cursor-zoom-in rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t17}</button>;
    $[36] = t13;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== c) {
    t19 = ideaLikeId(c);
    $[39] = c;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== c.id || $[42] !== c.title || $[43] !== t19) {
    t20 = <SaveHeartButton id={t19} altId={c.id} title={c.title} size="photo" className="absolute right-4 top-4" />;
    $[41] = c.id;
    $[42] = c.title;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== current || $[46] !== gallery) {
    t21 = gallery.length > 1 && <div aria-hidden="true" className="pointer-events-none absolute inset-x-4 bottom-0 flex h-[26px] items-center justify-center gap-2">{gallery.map((img_0, i) => <span key={`${img_0.url}-${i}`} className={cn("size-1.5 rounded-full bg-white", i === current ? "opacity-65" : "opacity-25")} />)}</div>;
    $[45] = current;
    $[46] = gallery;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t18 || $[49] !== t20 || $[50] !== t21) {
    t22 = <div className="relative">{t18}{t20}{t21}</div>;
    $[48] = t18;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== current || $[53] !== gallery || $[54] !== landscape || $[55] !== t) {
    t23 = gallery.length > 1 && <div className="flex gap-0.5 overflow-x-auto">{gallery.map((img_1, i_0) => <button key={`${img_1.url}-${i_0}`} type="button" onClick={() => setActive(i_0)} aria-label={t("feed.viewImage", {
        defaultValue: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u0438\u0437\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u0435 {{n}}",
        n: i_0 + 1
      })} aria-current={i_0 === current} className={cn("relative shrink-0 overflow-hidden rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus", landscape ? "h-[65px] w-[116px]" : "h-[100px] w-[74px]")}><img src={img_1.url} alt="" className="size-full bg-osmix-gray-100 object-cover" />{i_0 !== current && <span className="absolute inset-0 bg-white/50 transition-opacity hover:opacity-0" />}</button>)}</div>;
    $[52] = current;
    $[53] = gallery;
    $[54] = landscape;
    $[55] = t;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t22 || $[58] !== t23) {
    t24 = <div className="flex min-w-0 flex-col gap-1">{t22}{t23}</div>;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== c.title) {
    t25 = <h1 className="ds-h3 text-ds-primary">{c.title}</h1>;
    $[60] = c.title;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== c.description) {
    t26 = c.description && <p className="ds-t3 text-black">{c.description}</p>;
    $[62] = c.description;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t25 || $[65] !== t26) {
    t27 = <div className="flex flex-col gap-[5px]">{t25}{t26}</div>;
    $[64] = t25;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  const t28 = activeImage?.url ?? c.cover;
  let t29;
  if ($[67] !== c || $[68] !== current || $[69] !== t28) {
    t29 = <IdeaActions collection={c} imageUrl={t28} imageIndex={current} />;
    $[67] = c;
    $[68] = current;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== t27 || $[72] !== t29) {
    t30 = <div className="flex flex-col gap-2.5">{t27}{t29}</div>;
    $[71] = t27;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== products || $[75] !== t) {
    t31 = products.isPending ? <Skeleton className="h-80 w-full rounded-[20px]" /> : products.error ? <ErrorState onRetry={() => products.refetch()} /> : products.data && products.data.length > 0 ? <IdeaProductsPanel products={products.data} /> : <EmptyState title={t("catalog.empty")} />;
    $[74] = products;
    $[75] = t;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== t30 || $[78] !== t31) {
    t32 = <div className="flex min-w-0 flex-col gap-5">{t30}{t31}</div>;
    $[77] = t30;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== t24 || $[81] !== t32) {
    t33 = <section className="grid items-start gap-5 lg:grid-cols-2">{t24}{t32}</section>;
    $[80] = t24;
    $[81] = t32;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== c.slug) {
    t34 = <SimilarIdeas currentSlug={c.slug} />;
    $[83] = c.slug;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== t11 || $[86] !== t33 || $[87] !== t34) {
    t35 = <div className="flex flex-col gap-5">{t8}{t11}{t33}{t34}</div>;
    $[85] = t11;
    $[86] = t33;
    $[87] = t34;
    $[88] = t35;
  } else {
    t35 = $[88];
  }
  let t36;
  if ($[89] !== origin || $[90] !== t35) {
    t36 = <IdeaOriginProvider value={origin}>{t35}</IdeaOriginProvider>;
    $[89] = origin;
    $[90] = t35;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  return t36;
}

// «Товары из идеи»: серая панель со списком товаров идеи — у каждого галочка
// «в корзину», выбор размера и «Просмотр»; внизу тёмная кнопка «N товаров ·
// сумма», которая кладёт в корзину всё отмеченное.
//
// A-23: размер обязателен у товаров, где их больше одного. Если такой товар
// отмечен, а размер не выбран, кнопка ничего не кладёт: под его списком
// размеров появляется красное «Выберите размер», и страница подводит к первому
// такому товару. Раньше в корзину уезжал первый попавшийся вариант. Товары без
// размеров и с единственным размером (он выбран сразу) кладутся как есть.
// Ничего не кладём частично: иначе после выбора размеров повторное нажатие
// задвоило бы уже добавленные товары.
//
// variant="modal" — та же панель внутри модалки «Идею в корзину» (избранное):
// без собственного серого фона и паддинга (их даёт модалка), заголовок 20px
// с действием справа (крестик), зазоры 20px, список прокручивается внутри
// колонки, тёмная кнопка прижата к низу.
function _temp(img) {
  return img.url;
}
export function IdeaProductsPanel({
  products,
  variant = "page",
  headerAction
}: {
  products: Product[];
  variant?: "page" | "modal";
  headerAction?: ReactNode;
}) {
  const modal = variant === "modal";
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const fmt = useFormatPrice();
  const add = useCart(s => s.add);
  const origin = useIdeaOrigin();

  // Everything starts selected (matches the design's filled checks); the buyer
  // deselects what they don't want before adding to the cart.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(products.map(p => p.id)));
  // Единственный размер выбирать не из чего — он сразу стоит в списке.
  const [sizes, setSizes] = useState<Record<string, string>>(() => Object.fromEntries(products.flatMap(p_0 => p_0.sizes.length === 1 && p_0.sizes[0] ? [[p_0.id, p_0.sizes[0]]] : [])));
  // Красные подсказки «Выберите размер» — только после попытки добавить.
  const [sizeCheck, setSizeCheck] = useState(false);
  const [busy, setBusy] = useState(false);
  // «Просмотр» открывает быстрый просмотр товара без ухода со страницы идеи.
  const [quickViewId, setQuickViewId] = useState<string | null>(null);
  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);else next.add(id);
    return next;
  });
  const chosen = products.filter(p_1 => selected.has(p_1.id));
  const total = chosen.reduce((sum, p_2) => sum + (pickVariant(p_2, sizes[p_2.id])?.price ?? p_2.price), 0);
  const missingSize = chosen.filter(p_3 => needsSize(p_3) && !sizes[p_3.id]);
  const addSelected = async () => {
    if (chosen.length === 0 || busy) return;
    if (missingSize.length > 0) {
      setSizeCheck(true);
      document.getElementById(rowDomId(missingSize[0]?.id ?? ""))?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return;
    }
    setBusy(true);
    // До add(): событие «в корзину» шлёт корзина, идею она берёт из привязки.
    rememberIdeaOrigin(chosen.map(p_4 => p_4.id), origin);
    try {
      // Последовательно, НЕ Promise.all: add() читает-меняет одну корзину, и
      // параллельные вызовы клобберят друг друга (в корзину попадал 1 товар).
      for (const p_5 of chosen) {
        const size = sizes[p_5.id];
        const variant_0 = pickVariant(p_5, size);
        await add({
          productId: p_5.id,
          variantId: variant_0?.id,
          size: variant_0?.size ?? size ?? p_5.sizes[0] ?? "",
          color: variant_0?.color ?? p_5.color,
          qty: orderQty(p_5)
        });
      }
      setSizeCheck(false);
      notifyAddedToCart(t, () => navigate("/cart"), chosen.length === 1 ? {
        photo: chosen[0]?.photos[0],
        brand: chosen[0]?.brand,
        name: chosen[0]?.name
      } : {
        photo: chosen[0]?.photos[0]
      }, chosen.length);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  return <div className={cn("flex flex-col", modal ? "min-h-0 flex-1 gap-5" : "gap-3 rounded-[20px] bg-[#F8F8F8] p-4")}>
			{modal ? <div className="flex h-6 shrink-0 items-center justify-between gap-3">
					<h2 className="text-[20px] font-medium leading-6 text-[#161616]">
						{t("feed.productsInIdea", {
          defaultValue: "Товары из идеи"
        })}
					</h2>
					{headerAction}
				</div> : <h2 className="ds-h4 text-ds-ink">
					{t("feed.productsInIdea", {
        defaultValue: "Товары из идеи"
      })}
				</h2>}

			{/* На странице строки товаров 351 в ширину, как в макете (панель шире —
       справа остаётся воздух); на телефоне — во всю ширину. */}
			<div className={cn("flex flex-col gap-2.5", modal ? "min-h-0 flex-1 overflow-y-auto overscroll-contain" : "sm:max-w-[351px]")}>
				{products.map(p_6 => <IdeaProductRow key={p_6.id} product={p_6} selected={selected.has(p_6.id)} onToggle={() => toggle(p_6.id)} size={sizes[p_6.id]} onSize={s_0 => setSizes(prev_0 => ({
        ...prev_0,
        [p_6.id]: s_0
      }))} sizeError={sizeCheck && selected.has(p_6.id) && needsSize(p_6) && !sizes[p_6.id]} onPreview={() => {
        // Быстрый просмотр внутри идеи — тот же переход в карточку.
        rememberIdeaOrigin(p_6.id, origin);
        if (origin) trackProductView(p_6.id, origin);
        setQuickViewId(p_6.id);
      }} />)}
			</div>

			<QuickViewModal productId={quickViewId} open={quickViewId != null} onOpenChange={open => {
      if (!open) setQuickViewId(null);
    }} />

			{/* Кнопка по макету: 48 в высоту, r12, #1F1F1F, поля 20, иконка 20 и
       подпись 16/21 w600 «4 товара 25 000 ₽» через 8; на странице — по
       содержимому (не уже 268), на телефоне и в модалке — во всю ширину. */}
			<Button size="lg" block onClick={addSelected} disabled={chosen.length === 0 || busy} aria-busy={busy} className={cn(!modal && "sm:w-fit sm:min-w-[268px]")}>
				<BagIcon size={20} />
				<span>
					{t("feed.items", {
          count: chosen.length
        })} {fmt(total)}
				</span>
			</Button>
		</div>;
}

/** Размер нужно выбрать, только если их больше одного. */
const needsSize = (p: Product): boolean => p.sizes.length > 1;

/** Вариант для корзины: выбранного размера, цвета с фото — и в наличии. */
const pickVariant = (p: Product, size?: string) => {
  const variants = p.variants ?? [];
  if (variants.length === 0) return undefined;
  const pool = size ? variants.filter(v => v.size === size) : variants;
  return pool.find(v => v.color === p.color && v.stock > 0) ?? pool.find(v => v.stock > 0) ?? pool[0] ?? variants[0];
};

/** Размер, которого нет в наличии ни в одном варианте, не выбрать. */
const sizeInStock = (p: Product, size: string): boolean => {
  const variants = p.variants ?? [];
  return variants.length === 0 || variants.some(v => v.size === size && v.stock > 0);
};
const rowDomId = (productId: string) => `idea-product-${productId}`;

/** Размер одной строкой, как на плитке товара: «42 (XS)», «One size (44-54)». */
const sizeText = (p: Product, size: string): string => {
  const {
    main,
    sub
  } = sizeChipLabel(size, p.variants ?? []);
  return sub ? `${main} (${sub})` : main;
};

// Строка товара (кадр «Вертикальная идея 2»): белая плашка r20 с полями 8,
// фото 100×123 r16, в нём сердечко 30×30 (в 4 от угла) и «Просмотр» 78×18
// (10/13 w600, белая 50 %); справа бренд 14/14 и название 12/12 #ABABAB через
// 3, через 8 цена 14/14 w700 #333333, внизу «Выбрать размер» (32, r8, серая
// #989AA2 12 %, 12/16 w600, стрелка 16); галочка 20×20 в правом верхнем углу.
export function IdeaProductRow(t0) {
  const $ = _c(76);
  const {
    product: p,
    selected,
    onToggle,
    size,
    onSize,
    onPreview,
    sizeError: t1
  } = t0;
  const sizeError = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  let t2;
  if ($[0] !== p.id) {
    t2 = s => s.has(p.id);
    $[0] = p.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const liked = useWishlist(t2);
  const toggleProduct = useWishlist(_temp2);
  const setProductLikedOnServer = useWishlist(_temp3);
  const origin = useIdeaOrigin();
  const price = pickVariant(p, size)?.price ?? p.price;
  const errorId = `${rowDomId(p.id)}-size-error`;
  let t3;
  if ($[2] !== p.id || $[3] !== setProductLikedOnServer || $[4] !== size || $[5] !== toggleProduct) {
    t3 = () => {
      const next = toggleProduct(p.id, size);
      setProductLikedOnServer(p.id, next, size);
    };
    $[2] = p.id;
    $[3] = setProductLikedOnServer;
    $[4] = size;
    $[5] = toggleProduct;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const like = t3;
  let t4;
  if ($[7] !== p.id) {
    t4 = rowDomId(p.id);
    $[7] = p.id;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== origin || $[10] !== p.id) {
    t5 = productHref(p.id, origin);
    $[9] = origin;
    $[10] = p.id;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== origin || $[13] !== p.id) {
    t6 = () => rememberIdeaOrigin(p.id, origin);
    $[12] = origin;
    $[13] = p.id;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== p.name || $[16] !== p.photos[0]) {
    t7 = <img src={p.photos[0]} alt={p.name} loading="lazy" className="size-full rounded-[16px] bg-osmix-gray-100 object-cover" />;
    $[15] = p.name;
    $[16] = p.photos[0];
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <Link to={t5} onClick={t6} className="block size-full rounded-[16px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t7}</Link>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t) {
    t9 = t("feed.save", {
      defaultValue: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C"
    });
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== liked) {
    t10 = <FavoriteHeartIcon active={liked} size={30} />;
    $[24] = liked;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== like || $[27] !== liked || $[28] !== t10 || $[29] !== t9) {
    t11 = <button type="button" aria-pressed={liked} onClick={like} aria-label={t9} className="absolute right-1 top-1 flex size-[30px] items-center justify-center rounded-[4px] outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ds-focus">{t10}</button>;
    $[26] = like;
    $[27] = liked;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t) {
    t12 = t("feed.preview", {
      defaultValue: "\u041F\u0440\u043E\u0441\u043C\u043E\u0442\u0440"
    });
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== onPreview || $[34] !== t12) {
    t13 = <button type="button" onClick={onPreview} className="absolute bottom-0.5 left-1/2 flex h-[18px] w-[78px] -translate-x-1/2 items-center justify-center rounded-[4px] bg-white/50 text-[10px] font-semibold leading-[13px] text-ds-primary outline-none transition-colors hover:bg-white/80 focus-visible:ring-2 focus-visible:ring-ds-focus">{t12}</button>;
    $[33] = onPreview;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t13 || $[38] !== t8) {
    t14 = <div className="relative h-[123px] w-[100px] shrink-0">{t8}{t11}{t13}</div>;
    $[36] = t11;
    $[37] = t13;
    $[38] = t8;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== p.brand) {
    t15 = <p className="truncate text-[14px] leading-[14px] tracking-[-0.42px] text-ds-primary">{p.brand}</p>;
    $[40] = p.brand;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== p.name) {
    t16 = <p className="truncate text-[12px] leading-[12px] tracking-[-0.36px] text-ds-muted">{p.name}</p>;
    $[42] = p.name;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t15 || $[45] !== t16) {
    t17 = <div className="flex min-w-0 flex-col gap-[3px]">{t15}{t16}</div>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== fmt || $[48] !== price) {
    t18 = fmt(price);
    $[47] = fmt;
    $[48] = price;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t18) {
    t19 = <p className="text-[14px] font-bold leading-[14px] tracking-[-0.42px] text-ds-ink-3">{t18}</p>;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t17 || $[53] !== t19) {
    t20 = <div className="flex min-w-0 flex-col gap-2">{t17}{t19}</div>;
    $[52] = t17;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== onToggle || $[56] !== p.name || $[57] !== selected) {
    t21 = <CheckToggle selected={selected} onToggle={onToggle} label={p.name} />;
    $[55] = onToggle;
    $[56] = p.name;
    $[57] = selected;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t20 || $[60] !== t21) {
    t22 = <div className="flex items-start justify-between gap-2">{t20}{t21}</div>;
    $[59] = t20;
    $[60] = t21;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== errorId || $[63] !== onSize || $[64] !== p || $[65] !== size || $[66] !== sizeError || $[67] !== t) {
    t23 = p.sizes.length === 1 ? <div data-testid="idea-size-locked" aria-label={t("feed.sizeFixed", {
      defaultValue: "\u0420\u0430\u0437\u043C\u0435\u0440 {{size}}",
      size: sizeText(p, p.sizes[0] ?? "")
    })} className={cn(buttonVariants({
      variant: "gray",
      size: "sm"
    }), "pointer-events-none w-fit rounded-[8px]")}>{sizeText(p, p.sizes[0] ?? "")}</div> : p.sizes.length > 1 ? <div className="flex flex-col gap-1"><Select value={size} onValueChange={onSize}><SelectTrigger size="sm" aria-describedby={sizeError ? errorId : undefined} className={cn(buttonVariants({
          variant: "gray",
          size: "sm"
        }), "w-fit border-0 data-[size=sm]:rounded-[8px] data-[placeholder]:text-ds-primary [&_svg:not([class*='text-'])]:text-ds-primary")}><SelectValue placeholder={t("feed.selectSize", {
            defaultValue: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0440\u0430\u0437\u043C\u0435\u0440"
          })} /></SelectTrigger><SelectContent position="popper" align="start" sideOffset={-2} className="min-w-[142px]">{p.sizes.map(s_2 => <SelectItem key={s_2} value={s_2} disabled={!sizeInStock(p, s_2)}>{sizeText(p, s_2)}</SelectItem>)}</SelectContent></Select>{sizeError && <p id={errorId} role="alert" className="ds-t4 text-ds-error">{t("product.selectSize", {
          defaultValue: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0440\u0430\u0437\u043C\u0435\u0440"
        })}</p>}</div> : null;
    $[62] = errorId;
    $[63] = onSize;
    $[64] = p;
    $[65] = size;
    $[66] = sizeError;
    $[67] = t;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t22 || $[70] !== t23) {
    t24 = <div className="flex min-w-0 flex-1 flex-col justify-between gap-2">{t22}{t23}</div>;
    $[69] = t22;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== t14 || $[73] !== t24 || $[74] !== t4) {
    t25 = <div id={t4} className="relative flex gap-3 rounded-[20px] bg-white p-2">{t14}{t24}</div>;
    $[72] = t14;
    $[73] = t24;
    $[74] = t4;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  return t25;
}
function _temp3(s_1) {
  return s_1.setProductLikedOnServer;
}
function _temp2(s_0) {
  return s_0.toggleProduct;
}
export function CheckToggle(t0) {
  const $ = _c(7);
  const {
    selected,
    onToggle,
    label
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== label || $[1] !== t) {
    t1 = t("feed.selectForCart", {
      defaultValue: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \xAB{{name}}\xBB \u0434\u043B\u044F \u043A\u043E\u0440\u0437\u0438\u043D\u044B",
      name: label
    });
    $[0] = label;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onToggle || $[4] !== selected || $[5] !== t1) {
    t2 = <Checkbox shape="round" checked={selected} onCheckedChange={onToggle} aria-label={t1} />;
    $[3] = onToggle;
    $[4] = selected;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// «Похожие идеи» (макет: заголовок 20/24 w700, через 20 — стена с шагом 20).
// Скрытые покупателем идеи сюда не попадают (A-26).
function SimilarIdeas(t0) {
  const $ = _c(24);
  const {
    currentSlug
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["feed", "collections"],
      queryFn: _temp4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const feed = useQuery(t1);
  const visible = useVisibleIdeas(feed.data);
  const t2 = visible.ideas;
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[1] !== currentSlug || $[2] !== feed.isPending || $[3] !== t || $[4] !== visible.ideas || $[5] !== visible.pending) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t7;
      if ($[11] !== currentSlug) {
        t7 = c => c.slug !== currentSlug;
        $[11] = currentSlug;
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      const others = t2.filter(t7);
      if (feed.isPending || visible.pending || others.length === 0) {
        t6 = null;
        break bb0;
      }
      t4 = "mt-5 flex flex-col gap-5";
      let t8;
      if ($[13] !== t) {
        t8 = t("feed.similar", {
          defaultValue: "\u041F\u043E\u0445\u043E\u0436\u0438\u0435 \u0438\u0434\u0435\u0438"
        });
        $[13] = t;
        $[14] = t8;
      } else {
        t8 = $[14];
      }
      if ($[15] !== t8) {
        t5 = <h2 className="text-[20px] font-bold leading-6 tracking-[-0.6px] text-ds-primary">{t8}</h2>;
        $[15] = t8;
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      T0 = IdeaWall;
      t3 = others.map(_temp5);
    }
    $[1] = currentSlug;
    $[2] = feed.isPending;
    $[3] = t;
    $[4] = visible.ideas;
    $[5] = visible.pending;
    $[6] = T0;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    T0 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[17] !== T0 || $[18] !== t3) {
    t7 = <T0>{t3}</T0>;
    $[17] = T0;
    $[18] = t3;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t7) {
    t8 = <section className={t4}>{t5}{t7}</section>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}

// «‹ Назад» (макет: стрелка 24, через 6 подпись 14/18 w600) — туда, откуда
// пришли; если страницу открыли по ссылке — на стену идей.
function _temp5(c_0) {
  return <IdeaCard key={c_0.id} collection={c_0} />;
}
function _temp4() {
  return api.feed.collections();
}
function BackLink() {
  const $ = _c(9);
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  let t0;
  if ($[0] !== location.key || $[1] !== navigate) {
    t0 = () => location.key !== "default" ? navigate(-1) : navigate("/ideas");
    $[0] = location.key;
    $[1] = navigate;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChevronLeftIcon />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("feed.back", {
      defaultValue: "\u041D\u0430\u0437\u0430\u0434"
    });
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t2) {
    t3 = <button type="button" onClick={t0} className="flex h-6 w-fit items-center gap-1.5 rounded-sm ds-h5 text-ds-primary outline-none transition-colors hover:text-ds-accent focus-visible:ring-2 focus-visible:ring-ds-focus">{t1}{t2}</button>;
    $[6] = t0;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}